import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Mail, ArrowRight, Loader2, ShieldCheck, ArrowLeft, RotateCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function LoginPage({ onSuccess }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setIsSending(true);
    try {
      await api.post("/auth/mail", { email: email.trim() });
      setStep("code");
      toast.success("Code gesendet", { description: "Bitte prüfe dein E-Mail-Postfach." });
    } catch (error) {
      toast.error("Fehler", { description: error.response?.data?.detail || error.message });
    } finally {
      setIsSending(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setIsVerifying(true);
    try {
      const res = await api.post("/auth/verify", { email: email.trim(), code: code.trim() });
      if (res.data.success && res.data.token) {
        toast.success("Anmeldung erfolgreich");
        onSuccess(res.data.token);
      }
    } catch (error) {
      toast.error("Anmeldung fehlgeschlagen", { description: error.response?.data?.detail || error.message });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[var(--sa-light)] safe-area-top safe-area-bottom">
      {/* Header with Logo */}
      <header className="bg-white shadow-sm">
        <div className="px-6 py-4 flex items-center justify-center">
          <img src="/safety-assist-logo.png" alt="SafetyAssist" className="h-10 object-contain" />
        </div>
      </header>

      <main className="px-6 py-12 animate-slide-up">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-[var(--sa-primary)]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-[var(--sa-primary)]" />
            </div>
            <h1 className="text-3xl font-bold text-[var(--sa-secondary)] mb-3">Anmelden</h1>
            <p className="text-gray-600">
              {step === "email"
                ? "Melde dich mit deiner E-Mail-Adresse an. Wir senden dir einen Bestätigungscode."
                : `Wir haben einen 6-stelligen Code an ${email} gesendet.`}
            </p>
          </div>

          <Card className="p-6 bg-white shadow-lg border-0 rounded-2xl">
            {step === "email" ? (
              <form onSubmit={sendCode} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[var(--sa-secondary)]">E-Mail-Adresse</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      data-testid="login-email-input"
                      type="email"
                      autoComplete="email"
                      placeholder="name@firma.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-14 pl-12 pr-4 text-lg bg-[var(--sa-light)] border-0 rounded-xl focus:ring-2 focus:ring-[var(--sa-primary)]"
                      autoFocus
                    />
                  </div>
                </div>
                <Button
                  data-testid="login-send-code-btn"
                  type="submit"
                  disabled={!email.trim() || isSending}
                  className="w-full h-14 bg-[var(--sa-primary)] hover:bg-[var(--sa-primary-dark)] text-white rounded-xl font-semibold text-base btn-press"
                >
                  {isSending ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Sende Code...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>Code senden</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={verifyCode} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-[var(--sa-secondary)]">Bestätigungscode</label>
                  <Input
                    data-testid="login-code-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="______"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="h-16 text-center text-3xl font-mono tracking-[0.5em] bg-[var(--sa-light)] border-0 rounded-xl focus:ring-2 focus:ring-[var(--sa-primary)]"
                    autoFocus
                  />
                </div>
                <Button
                  data-testid="login-verify-btn"
                  type="submit"
                  disabled={code.length < 4 || isVerifying}
                  className="w-full h-14 bg-[var(--sa-primary)] hover:bg-[var(--sa-primary-dark)] text-white rounded-xl font-semibold text-base btn-press"
                >
                  {isVerifying ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Prüfe Code...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>Anmelden</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  )}
                </Button>

                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    data-testid="login-back-btn"
                    onClick={() => { setStep("email"); setCode(""); }}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[var(--sa-secondary)] transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    E-Mail ändern
                  </button>
                  <button
                    type="button"
                    data-testid="login-resend-btn"
                    onClick={sendCode}
                    disabled={isSending}
                    className="flex items-center gap-1.5 text-sm text-[var(--sa-primary)] hover:underline"
                  >
                    <RotateCw className={`w-4 h-4 ${isSending ? "animate-spin" : ""}`} />
                    Erneut senden
                  </button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </main>

      <footer className="p-4 pb-28 text-center">
        <p className="text-xs text-gray-400">© {new Date().getFullYear()} SafetyAssist</p>
      </footer>
    </div>
  );
}
